import { defineContract, listOf, nullableString, timestamp, z } from "./_base";

/*
 * The feed — what is happening with your contacts, and the proactive insight
 * queue. Verified against apps/api/src/routes/feed.ts and
 * packages/shared/src/proactive-intelligence.ts.
 */

export const feedItemSchema = z.object({
  id: z.string(),
  /* new_contact | score_change | relationship_added | journal_entry |
   * sync_event | engagement_due | ai_question — open set, so plain string. */
  type: z.string(),
  title: z.string(),
  description: z.string().optional().default(""),
  entityId: nullableString,
  entityName: nullableString,
  timestamp: timestamp,
  priority: z.enum(["high", "medium", "low"]).optional().default("low"),
  actionable: z.boolean().optional().default(false),
  action: nullableString,
  metadata: z.record(z.unknown()).nullish().transform((v) => v ?? {}),
});
export type FeedItem = z.infer<typeof feedItemSchema>;

export const feedContract = defineContract({
  name: "feed.list",
  method: "GET",
  path: () => "/feed",
  response: listOf(feedItemSchema),
});

/* ── Proactive insights ───────────────────────────────────────────────────
 * Cards the AI proposes. `structuredBlock` is markdown that may embed exactly
 * one `:::form {json} :::` block — the tap-to-answer input mechanism.
 */
export const proactiveSchema = z.object({
  id: z.string(),
  type: z.string(),
  title: z.string(),
  description: z.string().optional().default(""),
  priority: z.number().optional().default(3),
  metadata: z.record(z.unknown()).nullish().transform((v) => v ?? {}),
  structuredBlock: nullableString,
});
export type ProactiveInsight = z.infer<typeof proactiveSchema>;

export const proactiveContract = defineContract({
  name: "feed.proactive",
  method: "GET",
  path: () => "/feed/proactive",
  response: listOf(proactiveSchema),
});

export const dismissInsightContract = defineContract({
  name: "feed.proactive.dismiss",
  method: "POST",
  path: (input: { id: string }) => `/feed/proactive/${input.id}/dismiss`,
  response: z.unknown(),
});

export const actionInsightContract = defineContract({
  name: "feed.proactive.action",
  method: "POST",
  path: (input: { id: string; action: string; data?: Record<string, unknown> }) =>
    `/feed/proactive/${input.id}/action`,
  response: z.unknown(),
  params: (input) => ({ action: input.action, ...(input.data ? { data: input.data } : {}) }),
});

/* ── The :::form block ────────────────────────────────────────────────────
 * Emitted by proactive-intelligence.ts as
 *   text before
 *   :::form
 *   {"title":...,"fields":[...],"submitLabel":...,"submitAction":...,"data":{...}}
 *   :::
 */
export const formFieldSchema = z.object({
  id: z.string(),
  type: z.enum(["select", "text"]).catch("text"),
  label: z.string().optional().default(""),
  placeholder: nullableString,
  options: z.array(z.string()).optional(),
});
export type FormField = z.infer<typeof formFieldSchema>;

export const formBlockSchema = z.object({
  title: z.string().optional().default(""),
  fields: z.array(formFieldSchema).optional().default([]),
  submitLabel: z.string().optional().default("Save"),
  submitAction: z.string(),
  data: z.record(z.unknown()).optional().default({}),
});
export type FormBlock = z.infer<typeof formBlockSchema>;

/** Split a structuredBlock into prose + parsed form. A malformed form JSON is
 *  reported as null rather than thrown — the card still renders its text. */
export function parseStructuredBlock(block: string | undefined): {
  prose: string;
  form: FormBlock | null;
} {
  if (!block) return { prose: "", form: null };
  const m = block.match(/^([\s\S]*?):::form\s*\n([\s\S]*?)\n?:::\s*$/);
  if (!m) return { prose: block.trim(), form: null };
  try {
    const parsed = formBlockSchema.safeParse(JSON.parse(m[2]!.trim()));
    return { prose: m[1]!.trim(), form: parsed.success ? parsed.data : null };
  } catch {
    return { prose: m[1]!.trim(), form: null };
  }
}
